import { graphql, useStaticQuery } from 'gatsby';
import kebabCase from 'lodash.kebabcase';
import React, { useContext } from 'react';
import LazyLoad from 'react-lazyload';

import LayoutContext, {
  LayoutContextProps
} from '../../contexts/layoutContext';
import {
  Drupal_Entity,
  Drupal_ParagraphMarketoForm,
  FooterSocialIconsJsonEdge
} from '../../generated/graphql';
import { MenuLink } from '../../types/types';
import { getProcessedValue } from '../../utils/helpers';
import Link from '../link/link';
import './footer.scss';

export const footerMenus = {
  default: 'default-footer'
};

interface Props {
  padFooter?: boolean;
  menu: MenuLink[];
}

const PHONE_LINK = '8888751062';

const Footer = ({ padFooter, menu }: Props) => {
  const {
    language: { langPrefix },
    marketo: { getMarketoModal }
  } = useContext<LayoutContextProps>(LayoutContext);
  const { allFooterSocialIconsJson } = useStaticQuery(
    graphql`
      {
        allFooterSocialIconsJson {
          edges {
            node {
              name
              logo
              url
            }
          }
        }
      }
    `
  );
  const socialIcons = allFooterSocialIconsJson.edges as FooterSocialIconsJsonEdge[];

  return (
    <footer className={`bottom-footer ${padFooter ? 'cushion' : ''}`}>
      <div
        className="lucid-container lucid-container--flex
                    padding-t-sm padding-b-sm"
      >
        <div className="footer-nav">
          {menu.map(item => {
            return (
              <ul className="footer-links" key={item.title}>
                <li className="footer-links-heading">{item.title}</li>
                {item.links.map(subitem => {
                  let mktoId: string | null = null;
                  let mktoFields = {};
                  const marketoEntity = subitem.fieldMarketo
                    ? subitem.fieldMarketo.entity
                    : null;
                  if (marketoEntity) {
                    const match = (marketoEntity.entityTranslations! as Drupal_Entity[]).find(
                      ({ entityLanguage }) => entityLanguage!.id === langPrefix
                    );

                    if (match) {
                      const marketoForm = match as Drupal_ParagraphMarketoForm;
                      mktoId = marketoForm.fieldMarketoId
                        ? marketoForm.fieldMarketoId
                        : null;
                      mktoFields = {
                        title: marketoForm.fieldMarketoTitle,
                        body: getProcessedValue(marketoForm.fieldMarketoBody),
                        note: getProcessedValue(marketoForm.fieldMarketoNote)
                      };
                    }
                  }

                  if (mktoId) {
                    return (
                      <li className="footer-links__item" key={subitem.title}>
                        <button
                          className="footer-links__link"
                          type="button"
                          data-ga-trigger={`footer-${kebabCase(subitem.title)}`}
                          onClick={() => getMarketoModal(mktoId!, mktoFields)}
                        >
                          {subitem.title}
                        </button>
                      </li>
                    );
                  }
                  return (
                    <li className="footer-links__item" key={subitem.title}>
                      <Link
                        inverse
                        underline="none"
                        className="footer-links__link"
                        to={subitem.link.url!.path}
                        data-ga-trigger={`footer-${kebabCase(subitem.title)}`}
                      >
                        {subitem.title}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            );
          })}
        </div>
        <div className="footer-right">
          <div className="footer-right__top">
            <ul className="social-links">
              {socialIcons.map(socialIcon => {
                const icon = socialIcon.node;
                return (
                  <li key={icon.name!}>
                    <Link to={icon.url!} aria-label={`${icon.name!} link`}>
                      <LazyLoad offset={300}>
                        <img src={icon.logo!} alt={icon.name!} />
                      </LazyLoad>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="download-links padding-t-sm padding-b-sm">
            <Link
              aria-label="itunes app link"
              to="https://itunes.apple.com/us/app/lucidchart-flowchart-diagram-visio-viewer/id611543423?mt=8"
            >
              <LazyLoad offset={300}>
                <img
                  src="https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/footer/app-store.svg"
                  alt="Apple App Store"
                />
              </LazyLoad>
            </Link>
            <Link
              aria-label="google play store"
              to="https://play.google.com/store/apps/details?id=com.lucidchart.android.chart&amp;hl=en"
            >
              <LazyLoad offset={300}>
                <img
                  src="https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/footer/google-play.svg"
                  alt="Google Play Store"
                />
              </LazyLoad>
            </Link>
          </div>
          <div className="download-links padding-t-sm padding-b-sm">
            <img
              src="https://d2slcw3kip6qmk.cloudfront.net/Lucid4Edu/SiteAssets/parnter1.svg"
              alt="Google Premier Partner for Google for Education"
            />
          </div>

          <div className="footer-copyright padding-t-sm">
            <Link
              to={`tel:${PHONE_LINK}`}
              className="contact-sales js-phone-number"
            >
              Contact Education Sales: (888) 875-1062
            </Link>
            <p>
              <span role="img" aria-label="Copyright">
                &copy;
              </span>
              {` ${new Date().getFullYear()} Lucid Software Inc.`}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
